import {useSearchParams} from 'react-router-dom';

import {useAuthorize} from '@/hooks/useAuth';
import {useEntitledForClient} from '@/hooks/useEntitlements';
import EvacuationDialog from '@/shared/evacuation/components/EvacuationDialog';
import AddEventDialog from '@/shared/events/components/AddEventDialog';
import EditEventDialog from '@/shared/events/components/EditEventDialog';
import EventListDialog from '@/shared/events/components/EventListDialog';
import ExportDialog from '@/shared/export/components/ExportDialog';
import {useExportDefinitions} from '@/shared/export/hooks/useExportDefinitions';
import {EXPORT_PARAM, EXPORT_PARAMS} from '@/shared/export/utils/exportParams';
import UpdateSharedWarehouseItemStockDialog from '@/shared/inventory/components/UpdateSharedWarehouseItemStockDialog';
import ManageJobRolesDialog from '@/shared/job-roles/components/ManageJobRolesDialog';
import AddHealthLogMeasurementDialog from '@/shared/residents/components/AddHealthLogMeasurementDialog';
import AddHealthLogMeasurementsDialog from '@/shared/residents/components/AddHealthLogMeasurementsDialog';
import AddResidentMedicationDialog from '@/shared/residents/components/AddResidentMedicationDialog';
import EditHealthLogMeasurementDialog from '@/shared/residents/components/EditHealthLogMeasurementDialog';
import EditResidentMedicationDialog from '@/shared/residents/components/EditResidentMedicationDialog';
import MarkResidentAsAbsentDialog from '@/shared/residents/components/MarkResidentAsAbsentDialog';
import ResidentAmbulancePrintDialog from '@/shared/residents/components/ResidentAmbulancePrintDialog';
import ResidentContactsDialog from '@/shared/residents/components/ResidentContactsDialog';
import AddTaskDialog from '@/shared/tasks/components/dialogs/AddTaskDialog';
import EditRecurringTaskDialog from '@/shared/tasks/components/dialogs/EditRecurringTaskDialog';
import EditTaskDialog from '@/shared/tasks/components/dialogs/EditTaskDialog';
import PreviewTaskDialog from '@/shared/tasks/components/dialogs/PreviewTaskDialog';
import AddWorkTimeEntryDialog from '@/shared/work-time/components/AddWorkTimeEntryDialog';
import EditWorkTimeEntryDialog from '@/shared/work-time/components/EditWorkTimeEntryDialog';
import FinishWorkDialog from '@/shared/work-time/components/FinishWorkDialog';
import RejectWorkTimeEntryDialog from '@/shared/work-time/components/RejectWorkTimeEntryDialog';
import {useCanTrackWorkTime} from '@/shared/work-time/hooks/useCanTrackWorkTime';

const RootDialogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const viewLatestEvents = useAuthorize('VIEW_LATEST_EVENTS');
  const updateEvent = useAuthorize('UPDATE_EVENT');
  const createEvent = useAuthorize('CREATE_EVENT');
  const viewResidents = useAuthorize('VIEW_RESIDENTS');
  const updateTask = useAuthorize('UPDATE_TASK');
  const createTask = useAuthorize('CREATE_TASK');
  const createMedication = useAuthorize('CREATE_MEDICATION');
  const updateMedication = useAuthorize('UPDATE_MEDICATION');
  const createHealthlog = useAuthorize('CREATE_HEALTH_LOG_MEASUREMENT');
  const updateHealthlog = useAuthorize('UPDATE_HEALTH_LOG_MEASUREMENT');

  const updateStock = useEntitledForClient('INVENTORY_REPORTING');
  const manageWorkTime = useAuthorize('MANAGE_WORK_TIME');
  const canTrackWorkTime = useCanTrackWorkTime();
  const canExport = useExportDefinitions().length > 0;

  const isEventListOpen = !!searchParams.get('events');
  const isEditEventOpen = !!searchParams.get('editEvent');
  const isAddEventOpen = !!searchParams.get('addEvent');
  const isMarkResidentAsAbsentOpen =
    !!searchParams.get('markResidentAsAbsent') ||
    !!searchParams.get('markResidentAsPresent');
  const isExportOpen = !!searchParams.get(EXPORT_PARAM);
  const isResidentContactsOpen = !!searchParams.get('residentContacts');
  const isTaskOpen = !!searchParams.get('task');
  const isRecurringTaskOpen = !!searchParams.get('recurringTask');
  const isAddTaskOpen = !!searchParams.get('addTask');
  const isAddMedicationOpen = !!searchParams.get('addMedication');
  const isEditMedicationOpen = !!searchParams.get('editMedication');
  const isAddHealthLogOpen = !!searchParams.get('addHealthLog');
  const isAddHealthLogMeasurementsOpen = !!searchParams.get(
    'addHealthLogMeasurements'
  );
  const isEditHealthLogOpen = !!searchParams.get('editHealthLog');
  const isUpdateStockOpen = !!searchParams.get('updateStock');
  const isAmbulancePrintOpen = !!searchParams.get('ambulancePrint');
  const isFacilityEvacuationOpen = !!searchParams.get('facilityEvacuation');
  const isAddWorkTimeEntryOpen = !!searchParams.get('addWorkTimeEntry');
  const isFinishWorkOpen = !!searchParams.get('finishWork');
  const isRejectWorkTimeEntryOpen = !!searchParams.get('rejectWorkTimeEntry');
  const isEditWorkTimeEntryOpen = !!searchParams.get('editWorkTimeEntry');
  const isManageJobRolesOpen = !!searchParams.get('manageJobRoles');

  return (
    <>
      {isEventListOpen && viewLatestEvents && (
        <EventListDialog
          open={isEventListOpen}
          onOpenChange={() => {
            searchParams.delete('events');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isEditEventOpen && updateEvent && (
        <EditEventDialog
          open={isEditEventOpen}
          onOpenChange={() => {
            searchParams.delete('editEvent');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddEventOpen && createEvent && (
        <AddEventDialog
          open={isAddEventOpen}
          onOpenChange={() => {
            searchParams.delete('addEvent');
            searchParams.delete('pairingId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isMarkResidentAsAbsentOpen && (
        <MarkResidentAsAbsentDialog
          open={isMarkResidentAsAbsentOpen}
          onOpenChange={() => {
            searchParams.delete('markResidentAsAbsent');
            searchParams.delete('markResidentAsPresent');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isExportOpen && canExport && (
        <ExportDialog
          key={searchParams.get(EXPORT_PARAM)}
          open={isExportOpen}
          onOpenChange={() => {
            EXPORT_PARAMS.forEach(param => searchParams.delete(param));
            setSearchParams(searchParams);
          }}
        />
      )}
      {isResidentContactsOpen && viewResidents && (
        <ResidentContactsDialog
          open={isResidentContactsOpen}
          residentId={searchParams.get('residentId') ?? ''}
          onResidentChange={residentId => {
            setSearchParams({residentContacts: 'true', residentId});
          }}
          onOpenChange={() => {
            searchParams.delete('residentContacts');
            searchParams.delete('residentId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isTaskOpen && !updateTask && (
        <PreviewTaskDialog
          open={isTaskOpen}
          onOpenChange={() => {
            searchParams.delete('task');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isTaskOpen && updateTask && (
        <EditTaskDialog
          open={isTaskOpen}
          onOpenChange={() => {
            searchParams.delete('task');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isRecurringTaskOpen && updateTask && (
        <EditRecurringTaskDialog
          open={isRecurringTaskOpen}
          onOpenChange={() => {
            searchParams.delete('recurringTask');
            searchParams.delete('date');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddTaskOpen && createTask && (
        <AddTaskDialog
          open={isAddTaskOpen}
          onOpenChange={() => {
            searchParams.delete('addTask');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddMedicationOpen && createMedication && (
        <AddResidentMedicationDialog
          showResident
          open={isAddMedicationOpen}
          onOpenChange={() => {
            searchParams.delete('addMedication');
            searchParams.delete('residentId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isEditMedicationOpen && updateMedication && (
        <EditResidentMedicationDialog
          open={isEditMedicationOpen}
          onOpenChange={() => {
            searchParams.delete('editMedication');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddHealthLogOpen && createHealthlog && (
        <AddHealthLogMeasurementDialog
          open={isAddHealthLogOpen}
          onOpenChange={() => {
            searchParams.delete('addHealthLog');
            searchParams.delete('residentId');
            searchParams.delete('metricId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddHealthLogMeasurementsOpen && createHealthlog && (
        <AddHealthLogMeasurementsDialog
          open={isAddHealthLogMeasurementsOpen}
          onOpenChange={() => {
            searchParams.delete('addHealthLogMeasurements');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isEditHealthLogOpen && updateHealthlog && (
        <EditHealthLogMeasurementDialog
          open={isEditHealthLogOpen}
          onOpenChange={() => {
            searchParams.delete('editHealthLog');
            searchParams.delete('residentId');
            searchParams.delete('metricId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isUpdateStockOpen && updateStock && (
        <UpdateSharedWarehouseItemStockDialog
          open={isUpdateStockOpen}
          onOpenChange={() => {
            searchParams.delete('updateStock');
            searchParams.delete('itemId');
            searchParams.delete('warehouseId');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAmbulancePrintOpen && (
        <ResidentAmbulancePrintDialog
          open={isAmbulancePrintOpen}
          onOpenChange={() => {
            searchParams.delete('ambulancePrint');
            searchParams.delete('id');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isFacilityEvacuationOpen && (
        <EvacuationDialog
          open={isFacilityEvacuationOpen}
          onOpenChange={() => {
            searchParams.delete('facilityEvacuation');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isAddWorkTimeEntryOpen && manageWorkTime && (
        <AddWorkTimeEntryDialog
          open={isAddWorkTimeEntryOpen}
          onOpenChange={() => {
            searchParams.delete('addWorkTimeEntry');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isRejectWorkTimeEntryOpen && manageWorkTime && (
        <RejectWorkTimeEntryDialog
          open={isRejectWorkTimeEntryOpen}
          onOpenChange={() => {
            searchParams.delete('rejectWorkTimeEntry');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isEditWorkTimeEntryOpen && manageWorkTime && (
        <EditWorkTimeEntryDialog
          open={isEditWorkTimeEntryOpen}
          onOpenChange={() => {
            searchParams.delete('editWorkTimeEntry');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isFinishWorkOpen && canTrackWorkTime && (
        <FinishWorkDialog
          open={isFinishWorkOpen}
          onOpenChange={() => {
            searchParams.delete('finishWork');
            setSearchParams(searchParams);
          }}
        />
      )}
      {isManageJobRolesOpen && (
        <ManageJobRolesDialog
          open={isManageJobRolesOpen}
          onOpenChange={() => {
            searchParams.delete('manageJobRoles');
            setSearchParams(searchParams);
          }}
        />
      )}
    </>
  );
};

export default RootDialogs;
